/**
 * A company's saved details for Connect's use-case prompts — the pure half,
 * safe to import from client components. Stored one row per org in
 * `atlas.pdlc_org_context` (migrations/studio/pdlc_org_context.sql) by
 * `server/company-context-store.ts`.
 */

export interface CompanyContext {
    brand: string;
    competitors: string[];
    markets: string;
    /** The survey last joined, as its `NNNNN-Name` reference. */
    survey: string;
    /** The audience last described, e.g. "parents who buy QSR weekly". */
    audience: string;
    /** The category last named, e.g. "quick-service restaurants". */
    category: string;
}

export const COMPANY_CONTEXT_LIMITS = {
    brand: 120,
    competitor: 80,
    competitors: 10,
    markets: 200,
    survey: 200,
    audience: 200,
    category: 120,
} as const;

export const EMPTY_COMPANY_CONTEXT: CompanyContext = {
    brand: '',
    competitors: [],
    markets: '',
    survey: '',
    audience: '',
    category: '',
};

function cleanText(value: unknown, max: number): string {
    if (typeof value !== 'string') return '';
    return value.replace(/\s+/g, ' ').trim().slice(0, max);
}

/** Trims, caps and de-duplicates (case-insensitively) whatever arrives, so a
 * request body and a database row both come out in one canonical shape. */
export function normalizeCompanyContext(input: unknown): CompanyContext {
    const raw = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
    const seen = new Set<string>();
    const competitors: string[] = [];
    for (const item of Array.isArray(raw.competitors) ? raw.competitors : []) {
        const name = cleanText(item, COMPANY_CONTEXT_LIMITS.competitor);
        const key = name.toLowerCase();
        if (!name || seen.has(key)) continue;
        seen.add(key);
        competitors.push(name);
        if (competitors.length === COMPANY_CONTEXT_LIMITS.competitors) break;
    }
    return {
        brand: cleanText(raw.brand, COMPANY_CONTEXT_LIMITS.brand),
        competitors,
        markets: cleanText(raw.markets, COMPANY_CONTEXT_LIMITS.markets),
        survey: cleanText(raw.survey, COMPANY_CONTEXT_LIMITS.survey),
        audience: cleanText(raw.audience, COMPANY_CONTEXT_LIMITS.audience),
        category: cleanText(raw.category, COMPANY_CONTEXT_LIMITS.category),
    };
}

/** Where a use case's questions start: the saved row, or nothing. Only a
 * saved field counts as remembered (it decides "Last used" and whether a
 * brand must be asked); the org's own name is offered separately, as the
 * brand question's "Your company" option and default answer. */
export function startingCompanyContext(saved: CompanyContext | null): CompanyContext {
    return saved ?? EMPTY_COMPANY_CONTEXT;
}
